/**
 * Pure rasterization of an image-editor marquee into a binary mask.
 *
 * Tier 1 Group D (selection refinement) needs a shared primitive for
 * "give me the current selection as a Uint8Array I can read/write".
 * The same rasterization was already inlined in `invertMarquee` inside
 * useFabricCanvas — this module extracts it so Quick Mask, Color
 * Range, and Refine Edge can reuse a single tested path.
 *
 * Output convention matches the rest of the editor's mask code:
 *   `Uint8Array(width*height)`, 0 = unselected, 1 = selected
 *
 * Per-shape rasterization rules:
 *   - rect: scan-fill the clamped rect, scaleX/scaleY applied.
 *   - ellipse: scan-test (dx/rx)² + (dy/ry)² ≤ 1 in the clamped bbox.
 *   - lasso / polygon: even-odd polygon fill via `polygonPointsToMask`.
 *   - wand: read the wand's alpha mask canvas back via
 *     `canvasToBinaryMask`. If dims don't match the requested image
 *     dims, scaleblit through a transient canvas first.
 */
import { canvasToBinaryMask, polygonPointsToMask } from './mask-morphology';
import type { Point } from './marquee-bounds';

export type SelectionMaskInput =
  | {
      shape: 'rect';
      left: number;
      top: number;
      width: number;
      height: number;
      scaleX?: number;
      scaleY?: number;
    }
  | {
      shape: 'ellipse';
      left: number;
      top: number;
      rx: number;
      ry: number;
      scaleX?: number;
      scaleY?: number;
    }
  | { shape: 'lasso' | 'polygon'; points: Point[] }
  | { shape: 'wand'; maskCanvas: HTMLCanvasElement };

/** Rasterize a selection descriptor into a Uint8Array sized to the
 *  image's dimensions. Returns a zero-length array if `imgW` or
 *  `imgH` is non-positive — callers always check `.length` before
 *  reading.
 *
 *  Out-of-bounds geometry is silently clamped. A rect that extends
 *  past the canvas just gets its visible portion rasterized; that's
 *  the same convention `getImageData` uses and matches what the user
 *  sees on canvas. */
export function selectionToBinaryMask(
  input: SelectionMaskInput,
  imgW: number,
  imgH: number,
): Uint8Array {
  if (imgW <= 0 || imgH <= 0) return new Uint8Array(0);
  const mask = new Uint8Array(imgW * imgH);

  if (input.shape === 'rect') {
    const sX = input.scaleX ?? 1;
    const sY = input.scaleY ?? 1;
    const left = Math.max(0, Math.round(input.left));
    const top = Math.max(0, Math.round(input.top));
    const w = Math.min(imgW - left, Math.round(input.width * sX));
    const h = Math.min(imgH - top, Math.round(input.height * sY));
    for (let y = top; y < top + h; y++) {
      const row = y * imgW;
      for (let x = left; x < left + w; x++) {
        mask[row + x] = 1;
      }
    }
    return mask;
  }

  if (input.shape === 'ellipse') {
    const sX = input.scaleX ?? 1;
    const sY = input.scaleY ?? 1;
    const rx = input.rx * sX;
    const ry = input.ry * sY;
    if (rx <= 0 || ry <= 0) return mask;
    const cx = input.left + rx;
    const cy = input.top + ry;
    const x0 = Math.max(0, Math.floor(cx - rx));
    const x1 = Math.min(imgW - 1, Math.ceil(cx + rx));
    const y0 = Math.max(0, Math.floor(cy - ry));
    const y1 = Math.min(imgH - 1, Math.ceil(cy + ry));
    for (let y = y0; y <= y1; y++) {
      const row = y * imgW;
      const dy = (y - cy) / ry;
      const dy2 = dy * dy;
      for (let x = x0; x <= x1; x++) {
        const dx = (x - cx) / rx;
        if (dx * dx + dy2 <= 1) mask[row + x] = 1;
      }
    }
    return mask;
  }

  if (input.shape === 'lasso' || input.shape === 'polygon') {
    if (input.points.length < 3) return mask;
    const filled = polygonPointsToMask(input.points, imgW, imgH);
    // Defensive copy in case the polygon helper returns a different
    // length (it shouldn't, but its early-returns produce zero-length
    // arrays on invalid dims — we already guarded those above).
    const len = Math.min(mask.length, filled.length);
    for (let i = 0; i < len; i++) mask[i] = filled[i];
    return mask;
  }

  if (input.shape === 'wand') {
    const src = input.maskCanvas;
    if (src.width === imgW && src.height === imgH) {
      const result = canvasToBinaryMask(src);
      const len = Math.min(mask.length, result.mask.length);
      for (let i = 0; i < len; i++) mask[i] = result.mask[i];
      return mask;
    }
    // Mismatched dims — rescale via a transient canvas. This is the
    // same normalization `setSelectionFromMask` does on the reverse
    // path; keeps lib output dim-correct regardless of how the wand
    // was originally captured.
    const tmp = document.createElement('canvas');
    tmp.width = imgW;
    tmp.height = imgH;
    const ctx = tmp.getContext('2d', { willReadFrequently: true });
    if (!ctx) return mask;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(src, 0, 0, imgW, imgH);
    const result = canvasToBinaryMask(tmp);
    const len = Math.min(mask.length, result.mask.length);
    for (let i = 0; i < len; i++) mask[i] = result.mask[i];
    return mask;
  }

  return mask;
}

/** True when the mask has at least one set bit. Hot loop, written
 *  to bail on first hit. */
export function isMaskNonEmpty(mask: Uint8Array): boolean {
  for (let i = 0; i < mask.length; i++) if (mask[i]) return true;
  return false;
}

/** Bitwise NOT in place, scoped to the image rect. Faster than
 *  `mask.map(x => x ? 0 : 1)` (no allocation, no closure). */
export function invertMaskInPlace(mask: Uint8Array): void {
  for (let i = 0; i < mask.length; i++) mask[i] = mask[i] ? 0 : 1;
}

/** Combine two binary masks of equal length. Returns a new buffer;
 *  inputs untouched. Used by Quick Mask's add-to-selection brush
 *  ("union the painted stroke into the existing mask") and by
 *  Color Range's add-eyedropper sample. */
export function unionMasks(a: Uint8Array, b: Uint8Array): Uint8Array {
  const out = new Uint8Array(a.length);
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) out[i] = a[i] | b[i];
  return out;
}

/** Subtract `b` from `a` — pixels set in `b` are cleared in the
 *  result; pixels only in `a` are kept. Used by Quick Mask's
 *  eraser and by Color Range's subtract-eyedropper. */
export function subtractMasks(a: Uint8Array, b: Uint8Array): Uint8Array {
  const out = new Uint8Array(a.length);
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) out[i] = a[i] && !b[i] ? 1 : 0;
  return out;
}

/** Intersection — pixels set in both. */
export function intersectMasks(a: Uint8Array, b: Uint8Array): Uint8Array {
  const out = new Uint8Array(a.length);
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) out[i] = a[i] & b[i];
  return out;
}
